import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Repeat, Bookmark, Volume2 } from "lucide-react";
import { Page, BrandHeader } from "@/components/Brand";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useCards, usePrefs, useSavePrefs } from "@/lib/store";
import { playAffirmations, setMusicVolume, stopAffirmations } from "@/lib/affirmationAudio";
import { MorningCheck } from "@/components/MorningCheck";

export default function Affirmations() {
  const { data: cards } = useCards();
  const { data: prefs } = usePrefs();
  const savePrefs = useSavePrefs();
  const p = { music: 0.3, loop: false, ...(prefs?.affirmation || {}) };
  const [state, setState] = useState("idle");
  const [current, setCurrent] = useState(null);
  const list = cards ?? [];
  const active = state === "loading" || state === "playing";
  const update = (patch) => savePrefs.mutate({ affirmation: { ...p, ...patch } });

  useEffect(() => () => stopAffirmations(), []);

  const start = (from = 0) => {
    if (!list.length) return;
    setState("loading");
    const order = [...list.slice(from), ...(p.loop ? list.slice(0, from) : [])];
    playAffirmations(order.map((c) => c.text), {
      onIndex: (i) => { setState("playing"); setCurrent((from + i) % list.length); },
      onDone: () => { setState("idle"); setCurrent(null); },
      onError: () => { setState("error"); setCurrent(null); },
    }, p);
  };
  const stop = () => { stopAffirmations(); setState("idle"); setCurrent(null); };

  return (
    <Page>
      <div className="md:hidden"><BrandHeader /></div>
      <h1 className="mt-6 md:mt-4 text-4xl sm:text-5xl font-bold text-t-on tf-rise">Affirmations</h1>
      <p className="mt-2 text-t-on2 tf-rise tf-d1">Close your eyes. A calm voice reads your Healthy Adult scripts over gentle pan flute.</p>
      <MorningCheck />

      {list.length ? (
        <button onClick={() => (active ? stop() : start(0))} data-testid="affirmations-play"
          className="mt-8 w-full flex items-center gap-4 rounded-3xl bg-t-brand text-t-onbrand p-5 text-left shadow-[0_18px_40px_-20px_var(--brand)] tf-rise tf-d2 transition-transform active:scale-[0.99]">
          <span className="w-14 h-14 rounded-full bg-white/20 grid place-items-center">{active ? <Pause size={26} fill="currentColor" /> : <Play size={26} fill="currentColor" />}</span>
          <span>
            <span className="block text-lg font-bold">{active ? (state === "loading" ? "Preparing your session…" : "Listening…") : "Play all affirmations"}</span>
            <span className="block text-sm opacity-85">{list.length} card{list.length === 1 ? "" : "s"} · calm voice + pan flute{p.loop ? " · repeating" : ""}</span>
          </span>
        </button>
      ) : null}

      <div className="mt-6 tf-card p-5 flex flex-col gap-4">
        <div>
          <p className="text-sm font-semibold text-t-on2 mb-3">Pan flute volume</p>
          <Slider value={[p.music]} min={0} max={1} step={0.1} onValueChange={([v]) => { setMusicVolume(v); update({ music: v }); }} data-testid="flute-volume" />
        </div>
        <label className="flex items-center gap-3">
          <Repeat size={18} className="text-t-brand" />
          <span className="flex-1"><span className="block font-semibold text-t-on">Repeat continuously</span><span className="block text-[13px] text-t-muted">Keep cycling through your cards for a longer meditation</span></span>
          <Switch checked={p.loop} onCheckedChange={(v) => update({ loop: v })} disabled={active} data-testid="affirmations-loop" />
        </label>
      </div>
      {state === "error" ? <p className="mt-3 text-sm" style={{ color: "var(--hot)" }} data-testid="affirmations-error">Couldn't play. Check your connection and try again.</p> : null}

      <div className="mt-8 flex items-center justify-between">
        <p className="tf-tag">Your cards</p>
        <Link to="/cards" className="text-sm font-semibold text-t-brand" data-testid="manage-cards-link">Manage cards →</Link>
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {list.length === 0 ? (
          <div className="tf-card p-8 text-center text-t-muted" data-testid="affirmations-empty">
            <Bookmark size={34} className="mx-auto text-t-brand mb-3" />
            No affirmations yet. Save adaptive scripts from Flip the Script, or <Link to="/cards" className="text-t-brand font-semibold">write your own</Link>.
          </div>
        ) : list.map((c, i) => (
          <button key={c.id} onClick={() => start(i)} data-testid={`affirmation-${i}`}
            className={`tf-card p-5 text-left transition-colors ${current === i ? "!border-t-brand !bg-t-brand3" : "hover:border-t-border2"}`}>
            <span className="flex items-center justify-between">
              <span className="tf-tag">{c.title}{prefs?.affirmationCardId === c.id ? " · morning affirmation" : ""}</span>
              <Volume2 size={16} className={current === i ? "text-t-brand" : "text-t-muted"} />
            </span>
            <span className="block mt-2 text-lg text-t-on">“{c.text}”</span>
          </button>
        ))}
      </div>
    </Page>
  );
}
